<script setup lang="ts">
import { onMounted, ref, computed } from "vue";
import { UserService } from "../../../services/userService";
import { appContainer } from "../../../container";
import { LoadingService } from "../../../services/loadingService";
import { ParticipantDashboard } from "../../../interface/UserInterface";
import StatusCard from "../../../components/StatusCard.vue";

const emit = defineEmits(["close"]);

const userService = appContainer.resolve(UserService);
const loadingService = appContainer.resolve(LoadingService);

const props = defineProps<{
  participantId: number;
}>();

// TYPES & INTERFACES
type ParticipantDataKey = keyof ParticipantDashboard;

interface OverviewItem {
  title: string;
  data: number;
  icon: string;
}

// REACTIVE DATA
const participantData = ref<ParticipantDashboard>();
const isLoading = ref(true);

// CONSTANTS
/**
 * Maps participant data keys to their corresponding Bootstrap icons
 */
const iconMap: Partial<Record<ParticipantDataKey, string>> = {
  raters_invited: "bi-person",
  feedback_completion_count: "bi-check-circle",
  days_left: "bi-clock",
};

/**
 * Maps participant data keys to custom titles
 * Overrides backend titles for specific display requirements
 */
const titleMap: Partial<Record<ParticipantDataKey, string>> = {
  raters_invited: "Raters Invited",
  feedback_completion_count: "Completed Responses",
};

// COMPUTED PROPERTIES
/**
 * Extracts and formats table data for rater types (supervisor, self, direct_report, peer, other)
 * Filters out any invalid or incomplete data entries
 * @returns {Array} Array of rater type objects with title and data properties
 */
const tableData = computed(() => {
  if (!participantData.value) return [];

  const raterTypes: ParticipantDataKey[] = [
    "supervisor",
    "self",
    "direct_report",
    "peer",
    "other",
  ];

  return raterTypes
    .map((key) => participantData.value![key])
    .filter(
      (item) =>
        item && typeof item === "object" && "title" in item && "data" in item,
    );
});

/**
 * Prepares overview data for status cards display
 * Handles negative days_left values by converting them to 0
 * Only includes items that have corresponding icons defined in iconMap
 * @returns {OverviewItem[]} Array of overview items with title, data, and icon
 */
const overviewData = computed((): OverviewItem[] => {
  if (!participantData.value) return [];

  const overviewKeys: ParticipantDataKey[] = [
    "raters_invited",
    "feedback_completion_count",
    "days_left",
  ];

  return overviewKeys
    .map((key) => {
      const item = participantData.value![key];

      if (
        item &&
        typeof item === "object" &&
        "title" in item &&
        "data" in item &&
        iconMap[key]
      ) {
        let displayValue = item.data;
        if (key === "days_left") {
          const daysValue = Number(item.data);
          displayValue = isNaN(daysValue) || daysValue < 0 ? 0 : daysValue;
        }

        return {
          title: titleMap[key] || item.title,
          data: displayValue,
          icon: iconMap[key]!,
        };
      }
      return null;
    })
    .filter((item): item is OverviewItem => item !== null);
});

/**
 * Calculates and formats the total row data for the rater response table
 * Combines raters_invited and feedback_completion_count data
 * @returns {Object|null} Object with title, nominated, and responded counts or null if data unavailable
 */
const totalRowData = computed(() => {
  if (!participantData.value) return null;

  const ratersInvited = participantData.value.raters_invited;
  const feedbackCompletion = participantData.value.feedback_completion_count;

  if (!ratersInvited || !feedbackCompletion) return null;

  return {
    title: "Total",
    nominated: ratersInvited.data,
    responded: feedbackCompletion.data,
  };
});

// LIFECYCLE HOOKS
/**
 * Fetches participant dashboard data on component mount
 * Shows loading spinner during API call and handles cleanup
 */
onMounted(async () => {
  const loader = await loadingService.show();
  isLoading.value = true;

  try {
    await userService
      .getParticiapntDashboardData(props.participantId)
      .then((response) => {
        participantData.value = response;
      });
  } finally {
    await loader.hide();
    isLoading.value = false;
  }
});
</script>

<template>
  <div class="modal-backdrop">
    <div v-if="!isLoading" class="modal" tabindex="-1">
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content">
          <div class="modal-body">
            <h4 class="modal-title mb-4 bottom-line">Participant Dashboard</h4>

            <div class="body_content">
              <!-- SECTION 1: Overview Status Cards -->
              <div class="overview-section mb-4">
                <div class="overview-cards">
                  <StatusCard
                    v-for="item in overviewData"
                    :key="item.title"
                    :title="item.title"
                    :value="item.data"
                    :icon="item.icon"
                  />
                </div>
              </div>

              <!-- SECTION 2: Rater Types Table -->
              <div class="table-section">
                <h5 class="section-title">Rater Response Summary</h5>
                <div class="table-responsive">
                  <table class="table table-bordered text-center">
                    <thead class="table-light">
                      <tr>
                        <th class="rater-type-header">Rater Type</th>
                        <th class="rater-type-header">Invited</th>
                        <th class="rater-type-header">Responded</th>
                      </tr>
                    </thead>
                    <tbody>
                      <!-- Rater Type Rows -->
                      <tr
                        v-for="participant in tableData"
                        :key="participant.title"
                      >
                        <td class="rater-type-cell">{{ participant.title }}</td>
                        <td class="fw-semibold">
                          {{ String(participant.data).split("/")[1] }}
                        </td>
                        <td class="fw-semibold">
                          {{ String(participant.data).split("/")[0] }}
                        </td>
                      </tr>
                      <!-- Total Row -->
                      <tr v-if="totalRowData" class="total-table-data">
                        <td class="fw-bold text-white">
                          {{ totalRowData.title }}
                        </td>
                        <td class="fw-semibold text-white">
                          {{ totalRowData.nominated }}
                        </td>
                        <td class="fw-semibold text-white">
                          {{ totalRowData.responded }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <!-- SECTION 3: Action Buttons -->
            <div class="action-section">
              <div class="d-flex align-items-center justify-content-end p-2">
                <button
                  type="button"
                  class="btn btn-danger btn-sm text-white"
                  @click="emit('close')"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "../../../theme/variables";

.modal-backdrop {
  background-color: rgba(0, 0, 0, 0.5);
}

.modal-lg {
  max-width: 900px;
}

.modal-body {
  max-height: 80vh !important;
  display: flex;
  flex-direction: column;

  .modal-title {
    height: 42px;
    text-align: left;
  }

  .body_content {
    overflow: auto;
    scrollbar-width: thin;
  }
}

.section-title {
  color: #495057;
  font-weight: 600;
  margin-bottom: 1rem;
  border-bottom: 2px solid #e9ecef;
  padding-bottom: 0.5rem;
}

.overview-section {
  .overview-cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 0.5rem;
    justify-items: center;
  }
}

.value {
  font-family: Arial, Helvetica, sans-serif;
}

.table-section {
  .table {
    margin-bottom: 0;

    thead th {
      background-color: #f8f9fa;
      border-color: #dee2e6;

      &.rater-type-header {
        font-weight: normal;
      }

      &.fw-bold {
        font-weight: 600;
      }
    }

    tbody {
      td {
        vertical-align: middle;
        padding: 0.75rem;
      }

      .rater-type-cell {
        font-weight: normal;
      }

      .total-table-data {
        background-color: #4a84dd !important;

        td {
          background-color: $blue !important;
        }
      }
    }
  }
}

.action-section {
  border-top: 1px solid #e9ecef;
  margin-top: 1rem;

  button {
    min-width: 120px;
    padding: 0.5rem 2rem;
    font-weight: 500;

    &:hover {
      background-color: #c82333;
      border-color: #bd2130;
    }
  }
}

.bottom-line {
  border-bottom: 2px solid #e9ecef;
}

@media (max-width: 991px) {
  .modal-lg {
    max-width: 95%;
  }
}

@media (max-width: 767px) {
  .overview-section {
    .overview-cards {
      grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
      gap: 1rem;
    }
  }

  .action-section button {
    width: 100%;
  }
}

@media (max-width: 500px) {
  .overview-section {
    .overview-cards {
      grid-template-columns: 1fr;
      gap: 1rem;
      justify-content: start;
    }
  }
}
</style>
